import { castToSnapshot, getRoot, Instance, types } from 'mobx-state-tree';
import { VI } from '../VI';
import { Cropzone } from '@stores/Cropzone';
import * as turf from '@turf/turf';
import { SoilSample } from '../SoilSample';
import { ActivityAttachmentForm } from '@stores/AttachmentStore';
import { DateTimeType } from '@stores/DateTimeUtility';
import { IRootStoreAny } from '@stores/rootUtilType';
const LayerTab = types.model({
  layerType: types.identifier,
  isLoading: types.boolean
}).actions(self => ({
  setIsLoading(isLoading: boolean) {
    self.isLoading = isLoading;
  }
}));
type ILayerTab = Instance<typeof LayerTab>;
const MapLayerMap = types.model('MapLayerMap', {
  currentTab: types.reference(LayerTab),
  layers: types.array(LayerTab),
  selectedVI: types.safeReference(types.late(() => VI), {
    get(id: string, parent) {
      const uiStore = getRoot<IRootStoreAny>(parent).uiStore;
      return uiStore.lfmap.vis.find(x => x.id === id) || null;
    },
    set(value) {
      return value.id;
    }
  }),
  selectedSoilCollection: types.safeReference(types.model({
    id: types.string,
    datetimeZ: DateTimeType,
    soilTexture: types.string,
    datetime: DateTimeType,
    isSample: types.boolean
  }), {
    get(id: string, parent) {
      const uiStore = getRoot<IRootStoreAny>(parent).uiStore;
      return uiStore.lfmap.soilSamples.get(id);
    },
    set(value) {
      return value.id;
    }
  })
}).actions(self => ({
  setCurrentTab(tab: ILayerTab) {
    self.currentTab = tab;
  },
  setSelectedVI(VIId: string) {
    self.selectedVI = castToSnapshot(VIId);
  },
  clearSelectedVI() {
    self.selectedVI = undefined;
  },
  setSelectedSoilCollection(soilSampleId: string) {
    self.selectedSoilCollection = castToSnapshot(soilSampleId);
  },
  clearSelectedSoilCollection() {
    self.selectedVI = undefined;
  }
}));
export const MonitoringPage = types.model({
  mapLayerMap: types.maybe(MapLayerMap),
  selectedCropzone: types.safeReference(types.late(() => Cropzone), {
    get(id: string, parent) {
      const uiStore = getRoot<IRootStoreAny>(parent).uiStore;
      return uiStore.currentOrganization?.cropzones.get(id) || null;
    },
    set(value) {
      return value.id;
    }
  }),
  selectedSoilSample: types.safeReference(types.late(() => SoilSample), {
    get(id: string, parent) {
      const uiStore = getRoot<IRootStoreAny>(parent).uiStore;
      return uiStore.lfmap.soilSamples.get(id);
    },
    set(value) {
      return value.id;
    }
  }),
  searchQuery: types.string,
  showCropSidebar: false,
  userLocation: types.maybeNull(types.array(types.number)),
  distanceTrackerLocation: types.maybeNull(types.array(types.number)),
  observationForm: types.maybeNull(types.late(() => ActivityAttachmentForm)),
  soilObsDeleteConfModal: false,
  fertilizerDeleteConfModal: false
}).actions(self => ({
  setShowCropSidebar(isShowing: boolean) {
    self.showCropSidebar = isShowing;
  },
  setSelectedCropzone(cropzone) {
    self.selectedCropzone = cropzone;
  },
  setSearchQuery(query: string) {
    self.searchQuery = query;
  },
  setUserLocation(location: number[]) {
    self.userLocation = castToSnapshot(location);
  },
  setDistanceTrackerLocation(location: number[]) {
    self.distanceTrackerLocation = castToSnapshot(location);
  },
  setSelectedSoilSample(soilSampleId: string) {
    self.selectedSoilSample = castToSnapshot(soilSampleId);
  },
  setSoilObsDeleteConfModal(isVisible: boolean) {
    self.soilObsDeleteConfModal = isVisible;
  },
  setFertilizerDeleteConfModal(isVisible: boolean) {
    self.fertilizerDeleteConfModal = isVisible;
  },
  addForm(form: {
    id: string;
    activityID?: string;
  }) {
    return self.observationForm = ActivityAttachmentForm.create({
      id: form.id,
      activityID: form.activityID
    });
  },
  resetObservationForm() {
    self.observationForm = null;
  }
})).views(self => ({
  get userLocationString() {
    return self.userLocation && `${self.userLocation[0]},${self.userLocation[1]}`;
  },
  get userTraveledDistance() {
    const from = self.userLocation && turf.point(self.userLocation);
    const to = self.distanceTrackerLocation && turf.point(self.distanceTrackerLocation);
    return from && to ? turf.distance(from, to, {
      units: 'kilometers'
    }) * 1000 : 0;
  }
}));